import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, Save, Loader2, LifeBuoy, Copy, Link2, Trash2, Clock, QrCode } from "lucide-react";
import api from "../../lib/api";
import { Spinner } from "../common";
import AttachmentsPanel from "./AttachmentsPanel";

const FIELD_GROUPS = [
  { title: "Identity",       items: [
    ["display_name",       "Name on card"],
    ["preferred_language", "Preferred language"],
  ]},
  { title: "Medical basics", items: [
    ["blood_type",           "Blood type"],
    ["critical_allergies",   "Critical allergies (auto-pulled from Allergies list)"],
    ["current_medications",  "Current medications (auto-pulled from Medications list)"],
    ["conditions",           "Important conditions"],
  ]},
  { title: "Contacts",       items: [
    ["emergency_contact_name",  "Emergency contact name"],
    ["emergency_contact_phone", "Emergency contact phone"],
    ["physician_name",          "Physician name"],
    ["physician_phone",         "Physician phone"],
  ]},
  { title: "Insurance",      items: [
    ["health_insurance", "Health insurance"],
    ["travel_insurance", "Travel insurance"],
    ["instructions",     "Emergency instructions"],
  ]},
];

export default function EmergencyCardEditor({ onBack }) {
  const [card, setCard] = useState(null);
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ttlH, setTtlH] = useState(24);

  const load = () => Promise.all([
    api.get("/vault/health/emergency"),
    api.get("/vault/health/emergency/shares"),
  ]).then(([c, s]) => { setCard({ include_fields: [], ...c.data }); setShares(s.data.items || []); })
    .catch(() => { setCard({ include_fields: [] }); setShares([]); });

  useEffect(() => { load(); }, []);

  const set = (k, v) => setCard((c) => ({ ...c, [k]: v }));
  const toggle = (f) => setCard((c) => {
    const incl = new Set(c.include_fields || []);
    if (incl.has(f)) incl.delete(f); else incl.add(f);
    return { ...c, include_fields: Array.from(incl) };
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/vault/health/emergency", card);
      toast.success("Emergency card saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const createShare = async () => {
    try {
      await api.post(`/vault/health/emergency/share?ttl_hours=${ttlH}`);
      toast.success("Share link created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create share"); }
  };
  const revoke = async (token) => {
    if (!window.confirm("Revoke this share link? It will stop working immediately.")) return;
    try { await api.post(`/vault/health/emergency/shares/${token}/revoke`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };
  const copyLink = (token) => {
    const url = `${window.location.origin}/emergency/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  if (!card) return <Spinner />;
  const inp = "mt-1 w-full border border-zinc-300 px-3 py-2 text-sm";
  const activeShares = shares.filter((s) => !s.revoked_at && (!s.expires_at || s.expires_at > new Date().toISOString()));

  return (
    <div data-testid="emergency-card-editor">
      <button onClick={onBack} data-testid="emergency-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary mb-3">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-gradient-to-br from-rose-50 to-white border border-zinc-200 p-4 mb-5">
        <div className="flex items-center gap-3">
          <LifeBuoy className="w-6 h-6 text-rose-600" />
          <div>
            <h1 className="font-heading font-900 text-2xl">Emergency Health Card</h1>
            <p className="text-xs text-muted-foreground">You pick exactly what appears on the card. Nothing is shared until you create a link.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        {FIELD_GROUPS.map((g) => (
          <div key={g.title} className="border border-zinc-200 bg-white p-4">
            <h3 className="font-heading font-800 text-sm mb-3">{g.title}</h3>
            {g.items.map(([f, label]) => {
              const isAuto = f === "critical_allergies" || f === "current_medications";
              return (
                <label key={f} className="flex items-start gap-2 py-1.5 cursor-pointer" data-testid={`emergency-toggle-${f}`}>
                  <input type="checkbox" checked={(card.include_fields || []).includes(f)} onChange={() => toggle(f)} className="mt-0.5 w-4 h-4 accent-rose-600" />
                  <div className="flex-1">
                    <span className="text-sm">{label}</span>
                    {!isAuto && (card.include_fields || []).includes(f) && (
                      <input value={card[f] || ""} onChange={(e) => set(f, e.target.value)} data-testid={`emergency-input-${f}`}
                        className={inp} placeholder={`Enter ${label.toLowerCase()}…`} />
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid="emergency-save"
        className="w-full px-4 py-3 bg-emerald-600 text-white text-sm font-700 inline-flex items-center justify-center gap-1.5 disabled:opacity-60 mb-6">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-4 h-4" /> Save emergency card
      </button>

      <div className="mb-6">
        <AttachmentsPanel parentType="emergency" defaultTags={["emergency", "insurance"]} />
        <p className="text-[11px] text-muted-foreground mt-2 px-1">Attach insurance card photos, allergy epinephrine dose card, physician letter, etc. These stay private and are <b>never</b> included on your public emergency link.</p>
      </div>

      <div className="border border-zinc-200 bg-white p-4" data-testid="emergency-shares">
        <h3 className="font-heading font-800 text-sm mb-3 flex items-center gap-1.5"><Link2 className="w-4 h-4 text-primary" /> Share links</h3>
        <div className="flex items-center gap-2 mb-4">
          <select value={ttlH} onChange={(e) => setTtlH(Number(e.target.value))} data-testid="emergency-share-ttl" className="border border-zinc-300 px-2 py-1.5 text-sm">
            <option value={1}>1 hour</option>
            <option value={24}>24 hours</option>
            <option value={168}>7 days</option>
            <option value={720}>30 days</option>
          </select>
          <button onClick={createShare} data-testid="emergency-share-create" className="px-3 py-1.5 bg-primary text-white text-sm font-700">Create share link</button>
        </div>
        {activeShares.length === 0 ? (
          <p className="text-xs text-muted-foreground">No active share links. Create one above — first responders can scan it to see only the fields you've enabled.</p>
        ) : (
          <div className="space-y-2">
            {activeShares.map((s) => {
              const url = `${window.location.origin}/emergency/${s.token}`;
              return (
                <div key={s.token} className="border border-zinc-200 p-2 flex items-center justify-between gap-2 text-xs" data-testid={`emergency-share-${s.token}`}>
                  <div className="min-w-0">
                    <div className="truncate font-mono text-zinc-700">{url}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Expires {new Date(s.expires_at).toLocaleString()} · {s.access_count || 0} views</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copyLink(s.token)} data-testid={`emergency-share-copy-${s.token}`} className="p-1.5 border border-zinc-300 hover:border-primary" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
                    <a href={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="p-1.5 border border-zinc-300 hover:border-primary" title="QR code"><QrCode className="w-3.5 h-3.5" /></a>
                    <button onClick={() => revoke(s.token)} data-testid={`emergency-share-revoke-${s.token}`} className="p-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50" title="Revoke"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">Links contain an opaque token — no medical information is ever in the URL. Revoking is immediate.</p>
      </div>
    </div>
  );
}
